import { useMemo, useRef, useEffect, RefObject } from 'react';
import { isEqual } from 'lodash';
import {
  FullEventFragment,
  EventThemeConfigUpdateInput,
  useUpdateEventThemeConfigMutation,
} from '@/generated/graphql';
import { adminScreenThemeStateContext } from './AdminScreenThemeStateContext';
import { stripTypename } from '@/utils/stripTypename';
import { useCallback } from 'react';
import { ThemeSavedConfig } from '@/types/screen';
import { createLogger } from '@/utils/createLogger';
import { debounce } from 'lodash';
import { ActionType } from '@/providers/ScreenThemeStateProvider/actions';
import { useIframe } from '@/hooks/use-iframe';
import { setEngagementMode } from '@/providers/sharedActions';
import { toast } from 'sonner';
import { useScreenThemeState } from '@/providers/ScreenThemeStateProvider/ScreenThemeStateContext';

const logger = createLogger('AdminScreenThemeStateProvider');

/**
 * Convert the theme config to the input type for the updateEventThemeConfig mutation
 */
function themeConfigToInput(
  config: ThemeSavedConfig,
): EventThemeConfigUpdateInput {
  const { stageElements, fanElements, ...rest } = config;

  return {
    ...stripTypename(rest),
    stageElements: Object.values(stageElements).map((element) =>
      stripTypename(element),
    ),
    fanElements: Object.values(fanElements).map((element) =>
      stripTypename(element),
    ),
  };
}

export function AdminScreenThemeStateProvider({
  children,
  event,
  iframeRef,
}: {
  children: React.ReactNode;
  event: FullEventFragment;
  iframeRef?: RefObject<HTMLIFrameElement> | null;
}) {
  const [updateEventThemeConfig] = useUpdateEventThemeConfigMutation();

  // Create debounced version of onSave
  const debouncedOnSave = useMemo(
    () =>
      debounce((config: ThemeSavedConfig) => {
        logger.log('debouncedOnSave: ', {
          config,
        });
        updateEventThemeConfig({
          variables: {
            eventId: event.id,
            data: themeConfigToInput(config),
          },
          update: (cache, { data }) => {
            if (data?.updateEventThemeConfig) {
              cache.modify({
                id: cache.identify({ __typename: 'Event', id: event.id }),
                fields: {
                  themeConfig: () => data.updateEventThemeConfig,
                },
              });
            }
          },
          onError: (error) => {
            toast.error('Error saving theme config', {
              description: error.message,
            });
          },
        });
      }, 500),
    [event.id, updateEventThemeConfig],
  );

  const { state, dispatch } = useScreenThemeState();

  // iframe handling
  const handler = useCallback(
    (message: ActionType) => {
      logger.info('Dispatching message to admin theme state from iframe', {
        message,
      });
      dispatch(message);
    },
    [dispatch],
  );
  const [dispatchIframe] = useIframe(handler, { ref: iframeRef || undefined });

  // Also dispatch to the iframe
  const wrappedDispatch = useCallback(
    (message: ActionType) => {
      logger.info('Dispatching message to admin theme state from admin', {
        message,
      });
      dispatch(message);
      dispatchIframe(message);
    },
    [dispatch, dispatchIframe],
  );

  const lastSavedConfig = useRef(state.savedTheme);

  useEffect(() => {
    if (!isEqual(lastSavedConfig.current, state.savedTheme)) {
      logger.log('Saving theme config', state.savedTheme);
      debouncedOnSave(state.savedTheme);
      lastSavedConfig.current = state.savedTheme;
    } else {
      logger.log('No theme config change, not saving', state.savedTheme);
    }
  }, [debouncedOnSave, state.savedTheme]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      dispatchIframe(setEngagementMode({ mode: state.engagementMode }));
      isFirstRender.current = false;
    }
  }, [state.engagementMode, dispatchIframe]);

  const value = useMemo(
    () => ({
      state,
      dispatch: wrappedDispatch,
      event,
    }),
    [state, wrappedDispatch, event],
  );

  return (
    <adminScreenThemeStateContext.Provider value={value}>
      {children}
    </adminScreenThemeStateContext.Provider>
  );
}
